import React, { Fragment } from 'react';
import { useFormContext } from '../Provider';
import useValidators from '../Validators/useValidator';
import CommonFieldWrapper from './CommonFieldWrapper';
import { useGrants } from '../../../Data/Business';
import Tabs, { Tab } from '../../Containers/Tabs/Tabs';
import { classNames } from '../../../Services/helpers';
import { durationOptions } from './Duration';
import { useSession } from '../../Session';
import { daysOfWeek } from '../../../Data/Misc';

const dayTemplate = {
    name: null, // Replace with userId
    day: null, // Replace with day
    working: false,
    hours: {
        from: '09:00:00',
        to: '17:00:00'
    },
    breaks: [
        {
            duration: '01:00:00',
            between: {
                from: '09:00:00',
                to: '17:00:00'
            }
        }
    ]
};

const WorkingHours = ({label, name, help, onChange, disabled, validators, onlyDays}) => {
    const { formData, updateField, updateValidity, isValidating, isSubmitting, formDisabled } = useFormContext();
    const value = formData[name] ?? '';
    const {isValid, error} = useValidators(value, validators, name, updateValidity);
    const {activeGrants} = useGrants();
    const {hasFeature, user} = useSession();

    const set = (userId, day, field, val) => {
        const existing = extractDay(userId, day, value);

        let newValue;
        if (field === 'from') {
            const hours = { from: val, to: existing?.hours?.to ?? null };
            newValue = {...existing, hours, breaks: [{...existing.breaks[0], between: {...hours}}]};
        } else if (field === 'to') {
            const hours = { to: val, from: existing?.hours?.from ?? null };
            newValue = {...existing, hours, breaks: [{...existing.breaks[0], between: {...hours}}]};
        } else {
            newValue = {...existing, [field]: val};
        }

        const complete = {
            ...value,
            vehicles: value.vehicles.map(vehicle => {
                if (vehicle.name === userId) {
                    return {...vehicle, days: {...vehicle.days, [day]: newValue}};
                } else {
                    return vehicle;
                }
            })
        };

        if (!value.vehicles.some(vehicle => vehicle.name === userId)) {
            complete.vehicles.push({
                name: userId,
                days: {
                    ...daysOfWeek.reduce(
                        (acc, day) => ({...acc, [day]: {...dayTemplate, name: userId, day}}),
                        {}
                    ),
                    [day]: newValue
                }
            });
        }
        handleChange(complete)
    }

    const setBreak = (userId, val) => {
        const complete = {
            ...value,
            vehicles: value.vehicles.map(vehicle => {
                if (vehicle.name !== userId) {
                    return vehicle;
                }

                return {
                    ...vehicle,
                    days: Object.entries(vehicle.days ?? {}).reduce((acc, [dayKey, dayValue]) => {
                        if (!dayValue.working) {
                            return {
                                ...acc,
                                [dayKey]: dayValue
                            };
                        }
                        return {
                            ...acc,
                            [dayKey]: {
                                ...dayValue,
                                breaks: [{between: {...dayValue.hours}, duration: val === '' ? '00:00:00' : val}]
                            }
                        };
                    }, {})
                }
            })
        };
        handleChange(complete)
    }

    const handleChange = (val) => {
        updateField(name, val);
        onChange && onChange(val);
    }

    const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

    const isDisabled = formDisabled || disabled || isSubmitting;

    return <CommonFieldWrapper {...{label, name, help, value, isValid, isValidating, error, validators, isDisabled}}>
        {(hasFeature('ADDITIONAL_USERS') === true && activeGrants) && <Tabs compact>
            {activeGrants.map((grant, i) => {
                return <Tab key={i} id={grant.id} title={grant.title} compact>
                    <table className="w-full">
                        <tbody>
                            {days.map((day, i) => <DayRow key={i} {...{day, isDisabled, set, userId: grant.userId}}
                                value={extractDay(grant.userId, day, value)}
                                hide={onlyDays && !onlyDays.includes(day)}
                            />)}
                            <Break {...{isDisabled, userId: grant.userId, setBreak}} value={extractBreak(grant.userId, value)} />
                        </tbody>
                    </table>
                </Tab>
            })}
        </Tabs>}

        {(hasFeature('ADDITIONAL_USERS') === false && user) && <>
            <table className="w-full">
                <tbody>
                    {days.map((day, i) => <DayRow
                        key={i} {...{day, isDisabled, set, userId: user.id}}
                        value={extractDay(user.id, day, value)}
                        hide={onlyDays && !onlyDays.includes(day)}
                    />)}
                    <Break {...{isDisabled, userId: user.id, setBreak}} value={extractBreak(user.id, value)} />
                </tbody>
            </table>
        </>}

    </CommonFieldWrapper>
}

const extractDay = (userId, day, value) => {
    const vehicle = value.vehicles.find(vehicle => vehicle.name === userId);
    return vehicle?.days?.[day] ?? {...dayTemplate, userId, day};
}

const extractBreak = (userId, value) => {
    const vehicle = value.vehicles.find(vehicle => vehicle.name === userId);
    if (!vehicle || !vehicle?.days) {
        return null;
    }
    return Object.values(vehicle?.days ?? [])?.filter(day => day.working)?.[0]?.breaks?.[0]?.duration ?? null;
}

const DayRow = ({userId, day, value, isDisabled, set, hide}) => {
    return <tr className={classNames(hide && 'hidden')}>
        <td className="px-2 text-sm">
            {day.substr(0, 3)}
        </td>
        <td className="px-2 text-sm">
            <input
                id={'checkbox-day'}
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 text-brand focus:ring-brand disabled:bg-gray-200"
                checked={value.working}
                onChange={() => set(userId, day, 'working', !value.working)}
                disabled={isDisabled}
            />
        </td>
        <td className="px-2 text-sm">
            <input className={classNames(
                    'block w-full border-0 p-0 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6 disabled:text-gray-400',
                    // If it's disabled because the day is disabled it looks weird, only gray it out when the entire field is disabled
                    isDisabled && 'disabled:bg-gray-100',
                )}
                type="time"
                value={value.hours['from']}
                onChange={e => set(userId, day, 'from', e.target.value)}
                placeholder="From"
                disabled={isDisabled || !value.working}
            />
            {/* From */}
        </td>
        <td className="text-sm">
            -
        </td>
        <td className="px-2 text-sm">
            <input className={classNames(
                    'block w-full border-0 p-0 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6 disabled:text-gray-400',
                    // If it's disabled because the day is disabled it looks weird, only gray it out when the entire field is disabled
                    isDisabled && 'disabled:bg-gray-100',
                )}
                type="time"
                value={value.hours['to']}
                onChange={e => set(userId, day, 'to', e.target.value)}
                placeholder="To"
                disabled={isDisabled || !value.working}
            />
            {/* To */}
        </td>
    </tr>
}

const Break = ({userId, value, isDisabled, setBreak}) => {
    return <>
        <tr><td colSpan="5" className="h-1" /></tr>
        <tr>
            <td className="px-2 text-sm border-t pt-1 text-left" colSpan="2">
                Break
            </td>

            <td className="px-2 text-sm border-t pt-1" colSpan="3">
                <select
                    className="block w-full ml-1 p-0 border-0 border-transparent focus:border-transparent text-gray-900 sm:text-sm sm:leading-6 disabled:text-gray-400 disabled:bg-gray-100"
                    value={value ?? ''}
                    onChange={(v) => setBreak(userId, v.target.value)}
                    disabled={isDisabled}
                >
                    {durationOptions.map((option, index) => {
                        return <option key={index} value={option.value}>{option.label}</option>
                    })}
                </select>
            </td>
        </tr>
        <tr>
            <td colSpan="5">
                <p className="text-xs text-gray-400">
                    The break will be applied to all days where working hours are set.<br />
                    Break might be skipped if it allows jobs to be packed into fewer days.
                </p>
            </td>
        </tr>
    </>
}

export default WorkingHours;
